// JOBS: a workflow run with a `job` object (docs/agent-teams/build-spec.md).
// A requester (an agent or the operator) writes a brief; a Lead subagent
// plans it; lattice runs the plan on the workflow engine with a clock, the
// Lead revises at checkpoints, planned checks send faults back. Read and
// driven through routes/jobs.py. Every key sits under ["jobs", c]; every
// action also refreshes the workflow-run caches, since a job IS a run.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { workflowRunsKey } from "./workflowRuns";
import { isLiveRunStatus, workflowKeys } from "./workflows";

export type Brief = components["schemas"]["Brief"];
export type Deliverable = components["schemas"]["Deliverable"];
export type DeliverableKind = Deliverable["kind"];
export type AccessRef = components["schemas"]["AccessRef"];
export type AccessKind = AccessRef["kind"];
export type JobState = components["schemas"]["JobState"];
export type JobStatus = JobState["status"];
export type JobClock = components["schemas"]["JobClock"];
export type JobStepRow = components["schemas"]["JobStepRow"];
export type JobCheckRow = components["schemas"]["JobCheckRow"];
export type Revision = components["schemas"]["Revision"];
export type RevisionKind = Revision["kind"];
/** A refused plan attempt's team and steps, as the Lead wrote them (Revision.plan). */
export type PlanSketch = components["schemas"]["PlanSketch"];
/** Who reads a note sent now: the next checkpoint, the handover writer, the Lead's first checkpoint (while planning), or nobody. */
export type JobNoteReader = components["schemas"]["JobNoteReader"];
export type Fault = components["schemas"]["Fault"];
export type FaultSeverity = Fault["severity"];
export type PlanSpec = components["schemas"]["PlanSpec"];
export type TeamMember = components["schemas"]["TeamMember"];
export type JobSummary = components["schemas"]["JobSummary"];
export type JobListResponse = components["schemas"]["JobListResponse"];
export type StartJobBody = components["schemas"]["StartJobBody"];
export type JobActionResponse = components["schemas"]["JobActionResponse"];
export type JobNoteBody = components["schemas"]["JobNoteBody"];
export type JobTeamResponse = components["schemas"]["JobTeamResponse"];
/** Model tokens one node used through the Lattice model proxy (JobView.usage, by node id). */
export type NodeUsage = components["schemas"]["NodeUsage"];
/** Why a job failed, in plain words (JobView.failure; services/jobs/failure.py): the sentence, what to do, the technical reasons. */
export type JobFailure = components["schemas"]["JobFailure"];
/** The schema's JobView (one job, read whole); "Detail" here so the page component can be JobView. */
export type JobDetail = components["schemas"]["JobView"];

const base = (c: string) => `/api/customers/${encodeURIComponent(c)}/jobs`;
const one = (c: string, runId: string) => `${base(c)}/${encodeURIComponent(runId)}`;

export const jobKeys = {
  all: (c: string | null) => ["jobs", c] as const,
  list: (c: string | null, live: boolean) => ["jobs", c, "list", live ? "live" : "all"] as const,
  one: (c: string | null, runId: string | null) => ["jobs", c, "one", runId ?? ""] as const,
};

const notFound = (error: unknown) => error instanceof ApiError && error.status === 404;

/**
 * Jobs, newest first (`live` = only those still going), whether starting
 * one is offered here, and the job-team subagents the workspace lacks.
 * Polls every 10 s while a listed job is live, else every 30 s.
 */
export function useJobs(customerId: string | null, { live, limit }: { live?: boolean; limit?: number } = {}) {
  const q = new URLSearchParams();
  if (live) q.set("live", "true");
  if (limit) q.set("limit", String(limit));
  const qs = q.toString();
  return useQuery({
    queryKey: jobKeys.list(customerId, !!live),
    enabled: !!customerId,
    queryFn: ({ signal }) => apiFetch<JobListResponse>(`${base(customerId!)}${qs ? `?${qs}` : ""}`, { signal }),
    staleTime: 5_000,
    refetchInterval: (query) => ((query.state.data?.jobs ?? []).some((j) => isLiveRunStatus(j.run.status)) ? 10_000 : 30_000),
    refetchOnWindowFocus: true,
  });
}

/** One job: the brief, the clock, the plan, the steps, the checks, the revisions. Polled every 3 s while its run is live. */
export function useJob(customerId: string | null, runId: string | null) {
  return useQuery({
    queryKey: jobKeys.one(customerId, runId),
    enabled: !!customerId && !!runId,
    queryFn: ({ signal }) => apiFetch<JobDetail>(one(customerId!, runId!), { signal }),
    refetchInterval: (query) => (isLiveRunStatus(query.state.data?.run.status ?? "") ? 3_000 : false),
    refetchOnWindowFocus: true,
    staleTime: 2_000,
    // A missing job is an answer, not a blip to retry.
    retry: (count, error) => !notFound(error) && count < 1,
  });
}

/** The jobs list and every run cache (the Runs table, the run view): a job is a run. */
function invalidateJobs(qc: QueryClient, customerId: string) {
  void qc.invalidateQueries({ queryKey: jobKeys.all(customerId) });
  void qc.invalidateQueries({ queryKey: workflowRunsKey(customerId) });
  void qc.invalidateQueries({ queryKey: [...workflowKeys.all(customerId), "runs"] });
}

/** POST …/jobs: 403 jobs_not_enabled, 409 team_missing / runtime_unsupported, 422 no_time_limit. */
export function useStartJob() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, brief }: { customerId: string; brief: Brief }) =>
      apiFetch<JobDetail>(base(customerId), { method: "POST", body: { brief } satisfies StartJobBody }),
    onSuccess: (job, vars) => {
      qc.setQueryData(jobKeys.one(vars.customerId, job.run.run_id), job);
      invalidateJobs(qc, vars.customerId);
    },
  });
}

interface JobArgs {
  customerId: string;
  runId: string;
}

function useJobAction<V extends JobArgs>(path: (vars: V) => string, body?: (vars: V) => unknown) {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: (vars: V) => apiFetch<JobActionResponse>(path(vars), { method: "POST", body: body?.(vars) }),
    onSuccess: (res, vars) => qc.setQueryData(jobKeys.one(vars.customerId, vars.runId), res.job),
    onSettled: (_d, _e, vars) => invalidateJobs(qc, vars.customerId),
  });
}

/** Approve the Lead's plan (409 not_awaiting_approval once it is not waiting). */
export function useApproveJob() {
  return useJobAction<JobArgs>(({ customerId, runId }) => `${one(customerId, runId)}/approve`);
}

/** Stop the job: every live step is aborted (the run's cancel). Confirm first. */
export function useCancelJob() {
  return useJobAction<JobArgs>(({ customerId, runId }) => `${one(customerId, runId)}/cancel`);
}

/** A note the Lead reads at its next checkpoint, or the handover writer when none is left (409 not_live once the job has ended, no_reader once nobody would read it). */
export function useJobNote() {
  return useJobAction<JobArgs & { note: string }>(
    ({ customerId, runId }) => `${one(customerId, runId)}/message`,
    ({ note }) => ({ note }) satisfies JobNoteBody,
  );
}

/** Creates the job-team subagents the workspace lacks (the Lead, the writing lead, the checkers, the general worker). */
export function useInstallJobTeam() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId }: { customerId: string }) =>
      apiFetch<JobTeamResponse>(`/api/customers/${encodeURIComponent(customerId)}/job-team/install`, { method: "POST" }),
    onSettled: (res, _e, vars) => {
      invalidateJobs(qc, vars.customerId);
      if (res?.created?.length) {
        void qc.invalidateQueries({ queryKey: ["subagent-blueprints", vars.customerId] });
        void qc.invalidateQueries({ queryKey: ["configured-agents"] });
        void qc.invalidateQueries({ queryKey: ["customer-agents"] });
      }
    },
  });
}
